/**
 * Bound the `?email=` value of an invitation link to something that is
 * plausibly an address.
 *
 * An invitation link is `/register?invitation_id=…&email=…`, and both screens on
 * that path render the address as the PRODUCT's own statement — "Your invitation
 * was sent to X", "Log out and sign in as X", the latter on a primary button.
 * The value arrives from a URL anyone can craft, so free text there is a
 * social-engineering surface: `?email=Call+00+000+to+verify+your+account` puts an
 * attacker's sentence inside first-party chrome. `{{ }}` escapes markup, so this
 * was never injection — it is about who appears to be speaking.
 *
 * The character set is the HTML5 email-input one, near enough: no whitespace, no
 * angle brackets or quotes, but the punctuation real addresses use is kept — an
 * apostrophe in `o'brien@…`, a plus in a tagged address. Anything that fails is
 * treated as no address at all, and the screens fall back to their generic copy,
 * which is the behaviour that existed before any of this.
 */
const SHAPE = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/
const MAX_LENGTH = 254 // RFC 5321 forward-path limit, minus the angle brackets

export function invitedEmailFromQuery(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed || trimmed.length > MAX_LENGTH) return null
  return SHAPE.test(trimmed) ? trimmed : null
}

/**
 * The invitation context of a link (`invitation_id`, and the address when there
 * is one), ready to be carried onto the next route; `null` when the query holds
 * no invitation. One builder for every hop that forwards an invitation link, so
 * none of them drops the address the invitations page needs to tell a wrong
 * account from an empty list (MYAC-1230), and none drops the id it needs to
 * accept on the person's behalf (MYAC-1522).
 *
 * The address is trimmed, not shape-checked: every screen that renders it reads
 * it back through `invitedEmailFromQuery`.
 */
export function invitationLinkQuery(query: Record<string, unknown>): Record<string, string> | null {
  const invitationId = query.invitation_id
  if (typeof invitationId !== 'string' || !invitationId) return null
  const forwarded: Record<string, string> = { invitation_id: invitationId }
  const email = query.email
  if (typeof email === 'string' && email.trim()) forwarded.email = email.trim()
  return forwarded
}
